import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useIntersect } from "@/hooks/useIntersect";
import { isProInteractionViewport } from "./pro-interaction-viewport";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";

const MobileGNB = 48;
const DesktopGNB = 60;

interface Props {
  pageLength?: number;
  canvansRef?: React.RefObject<HTMLCanvasElement | null>;
  className?: string;
}

export default function ProInteractionA({
  canvansRef: _canvasRef,
  pageLength = 4,
  className,
}: Props) {
  const { t } = useTrans(["tactsuit"]);

  const isMobile = useRef<Boolean>(false);
  const canvasOuterRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(_canvasRef?.current ?? null);
  const videoOuterRef = useRef<HTMLDivElement>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  /**
   * 대체 영상(3.3MB)의 `src` 게이트. **`null` = 아직 판정 전**(SSR·최초 렌더)이다.
   *
   * `boolean` 초기값을 쓰면 안 된다 — `false`(캔버스 꺼짐)로 시작하면 `src` 가 **SSR
   * HTML 에 박혀서**, 데스크톱에서 캔버스가 켜지든 말든 브라우저가 HTML 파싱 시점에
   * 받기 시작한다(실측: 시작 690ms · 3.29MB 완전 버퍼 · `parentDisplay: none`).
   * 하이드레이션 뒤에 언마운트해도 이미 늦다.
   *
   * `<video>` 엘리먼트 자체는 항상 렌더한다 — `src` 만 빼면 `h-[240px]` 박스가
   * 유지돼 모바일에서 레이아웃 시프트가 없다.
   */
  const [canvasActive, setCanvasActive] = useState<boolean | null>(null);

  const imagesRef = useRef<HTMLImageElement[]>([]);

  // 이미지를 준비합니다.
  const updateImages = () => {
    const proInteractionAImages = Array(163)
      .fill(0)
      .map((_, i) => {
        const index = `${i + 1}`.padStart(3, "0");
        return `/images/tactsuit/tactsuit-pro/pro-interaction-a/pro-interaction-${index}.webp`;
      });

    const images = proInteractionAImages.map((url) => {
      const image = new Image();
      image.src = url;
      return image;
    });

    // 스크롤 하지 않은 경우 이미지가 다 로드된 시점에서 onScroll 를 해서 캔버스에 그리기
    Promise.all(
      images.map(
        (img) => new Promise<void>((resolve) => (img.onload = () => resolve())),
      ),
    ).then(() => {
      imagesRef.current = images;
      onScroll();
    });
  };

  // 이미지를 그립니다
  const onRequest = useCallback((index: number) => {
    const image = imagesRef.current[index];
    const ctx = canvasRef.current?.getContext("2d");

    if (!!ctx && image && !!canvasRef.current) {
      const canvasWidth = canvasRef.current.width;
      const canvasHeight = canvasRef.current.height;
      const canvasRatio = canvasWidth / canvasHeight;
      const imageRatio = image.naturalWidth / image.naturalHeight;
      let renderWidth, renderHeight, xOffset, yOffset;
      // 이미지가 더 넓은 경우
      if (imageRatio > canvasRatio) {
        renderHeight = canvasHeight;
        renderWidth = canvasHeight * imageRatio;
        // center
        xOffset = (canvasWidth - renderWidth) / 2;
        yOffset = 0;
      } else {
        renderWidth = canvasWidth;
        renderHeight = canvasWidth / imageRatio;
        // center
        yOffset = 0;
        xOffset = 0;
      }
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      ctx.drawImage(image, xOffset, yOffset, renderWidth, renderHeight);
    }
  }, []);

  const onScroll = useCallback(() => {
    if (!!canvasRef.current && !!canvasOuterRef.current) {
      const offset = isMobile.current ? MobileGNB : DesktopGNB;
      const canvasHeight = canvasRef.current.height;
      const outerBound = canvasOuterRef?.current?.getBoundingClientRect();
      // 캔버스가 화면 가장 상단에 있을 시점부터
      const from = -outerBound.top + offset;
      const to = outerBound.height - canvasHeight;
      let ratio = from / to;
      ratio = Math.min(Math.max(ratio, 0), 1);
      const len = imagesRef.current.length;
      const index = Math.min(len - 1, Math.ceil(ratio * len));
      setCurrentImageIndex(index);
      requestAnimationFrame(() => onRequest(index));
    }
  }, []);

  const onTrigger = useCallback(
    ([entry]: any, observer: any) => {
      if (entry.isIntersecting) {
        window.addEventListener("scroll", onScroll);
      } else {
        window.removeEventListener("scroll", onScroll);
      }
    },
    [onScroll],
  );

  useIntersect(canvasRef, onTrigger, { threshold: 0 });

  useLayoutEffect(() => {
    const onResize = () => {
      // ref 가드보다 먼저 — 뷰포트 판정에는 ref 가 필요 없고, 이 값이 대체 영상
      // `src` 게이트다. 가드 뒤에 두면 판정이 `null` 로 남아 영상이 안 뜬다.
      setCanvasActive(isProInteractionViewport());

      if (
        !canvasOuterRef.current ||
        !canvasRef.current ||
        !videoOuterRef.current
      ) {
        return;
      }

      /**
       * 좁거나(비율 미달) 초광폭인 화면에서는 캔버스를 숨기고 대체 영상을 보여줍니다.
       * 판정은 pro-interaction-viewport 단일 출처 — 다음 섹션의 -mt-[100vh] 겹침이
       * 같은 조건을 봐야 어긋나지 않는다.
       */
      if (!isProInteractionViewport()) {
        canvasOuterRef.current.style.display = "none";
        videoOuterRef.current.style.display = "block";
      } else {
        updateImages();
        canvasOuterRef.current.style.display = "block";
        videoOuterRef.current.style.display = "none";
      }

      if (canvasRef.current) {
        isMobile.current = window.innerWidth <= 1023;
        const offset = isMobile.current ? MobileGNB : DesktopGNB;
        canvasRef.current.style.top = `${offset}px`;
        canvasRef.current.height =
          document.documentElement.clientHeight - offset;
        canvasRef.current.width = document.documentElement.clientWidth;
      }
    };

    window.addEventListener("resize", onResize);
    onResize();

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <>
      <div
        ref={canvasOuterRef}
        className={cn(
          "relative hidden items-start justify-center bg-coolgray-10 laptop:flex",
          pageLengthObject[pageLength],
          className,
        )}
      >
        <div className="sticky top-[48px] laptop:top-[60px]">
          <canvas className="sticky block" ref={canvasRef} />
          <div className="mx-4 flex flex-col gap-3 laptop:absolute laptop:right-1/2 laptop:top-1/2 laptop:mx-5 laptop:mt-0 laptop:max-w-[300px] laptop:-translate-x-1/2 laptop:-translate-y-full laptop:gap-5 laptop:text-white">
            <h2
              className="text-3xl transition-opacity laptop:text-5xl"
              style={{
                opacity:
                  currentImageIndex - 10 <= 0
                    ? 0
                    : currentImageIndex - 120 > 0
                      ? 1 - (currentImageIndex - 120) * 0.1
                      : 1,
              }}
            >
              {t("Effortless Control")}
            </h2>
            <div className="relative w-[350px]">
              <p
                className="absolute text-base transition-opacity laptop:text-lg"
                // 현재 스크롤 위치에 따라서 텍스트를 보여주거나 가립니다.
                style={{
                  opacity:
                    currentImageIndex - 10 <= 0
                      ? 0
                      : currentImageIndex - 50 > 0
                        ? 1 - (currentImageIndex - 50) * 0.1
                        : 1,
                }}
              >
                {t(`Easily power on with the front controller.`)}
              </p>
              <p
                className="absolute text-base transition-opacity laptop:text-lg"
                style={{
                  opacity:
                    currentImageIndex - 80 <= 0
                      ? 0
                      : currentImageIndex - 120 > 0
                        ? 1 - (currentImageIndex - 120) * 0.1
                        : 1,
                }}
              >
                {t(
                  `Adjust haptic feedback intensity and freely control pause and play functions.`,
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div
        ref={videoOuterRef}
        className={cn(
          "relative laptop:hidden laptop:bg-coolgray-10",
          className,
        )}
      >
        <video
          // ⚠️ `key` 로 **엘리먼트를 새로 만든다**. `src` 만 나중에 꽂으면 이미
          // 마운트된 엘리먼트에 autoplay 가 다시 걸리는지가 브라우저 구현에 달린다
          // (헤드리스는 자동재생 정책상 전부 `paused` 라 검증이 불가능했다).
          // 새 엘리먼트는 `<video autoPlay src>` 초기 상태 = 변경 전과 동일하므로
          // 재생 동작이 구조적으로 보장된다.
          key={canvasActive === false ? "alt-video" : "alt-video-idle"}
          // 캔버스가 켜지는 뷰포트에서는 이 영상이 절대 보이지 않는다 → 받지 않는다.
          // `autoPlay` 는 `preload` 를 무력화하므로(실측: `preload="metadata"` 인데도
          // 5개 영상 전부 완전 버퍼) preload 조절로는 못 막고 `src` 를 빼야 한다.
          src={
            canvasActive === false
              ? "/media/tactsuit/tactsuit-pro/pro-interaction-a-alt-video.mp4"
              : undefined
          }
          controls={false}
          autoPlay
          playsInline
          muted
          loop
          className="mx-auto h-[240px] w-full max-w-[1440px] object-cover laptop:h-[800px]"
        />
        <div className="mx-4 mt-6 flex flex-col gap-3 laptop:absolute laptop:right-1/2 laptop:top-1/2 laptop:mx-5 laptop:mt-0 laptop:max-w-[300px] laptop:-translate-x-1/3 laptop:-translate-y-1/2 laptop:gap-5 laptop:text-white">
          <h2 className="text-3xl laptop:text-5xl">
            {t("Effortless Control")}
          </h2>
          <p className="text-base laptop:text-lg">
            {t(`Easily power on with the front controller.`)}
          </p>
          <p className="text-base laptop:text-lg">
            {t(
              `Adjust haptic feedback intensity and freely control pause and play functions.`,
            )}
          </p>
        </div>
      </div>
    </>
  );
}

const pageLengthObject = {
  1: "min-h-[100vh]",
  2: "min-h-[200vh]",
  3: "min-h-[300vh]",
  4: "min-h-[400vh]",
  5: "min-h-[500vh]",
};
